/**
 * HotelBooking.jsx - Hotel booking flow
 *
 * Phases:
 *   details    -> rooms + lead guest form + fare summary
 *   processing -> prebook + create_order
 *   polling    -> payment done, checking status
 *   done       -> confirmation / failure
 *
 * Props:
 *   hotel     - { hotel_code, hotel_name, hotel_rating, rooms[], selected_room,
 *                 check_in (yyyy-mm-dd), check_out (yyyy-mm-dd), no_of_rooms? }
 *   userId    - string
 *   hotelTest - bool
 *   onClose   - () => void
 *   fullPage  - bool (page layout vs bottom sheet)
 *
 * Booking reference:
 *   rc_booking_id returned by the hotel_details call is the single source of truth.
 *   It is sent as both rc_booking_id and ClientReferenceId on prebook and create_order,
 *   and used for status polling. Prebook's id is only a fallback if details had none.
 */

import { useState, useEffect, useRef } from "react";

/* ── Tokens ── */
const ACCENT = "#E8540C";
const ACCENT_DARK = "#C94707";
const INK = "#1A1A1A";
const MUTED = "#5F6368";
const LINE = "#E3E3E3";
const OK = "#137333";
const BAD = "#B3261E";
const WARN = "#B26A00";

const LAMBDA_URL = "https://dw5wuw157e.execute-api.ap-south-1.amazonaws.com";
const TBO_URL = "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development";

const PLATFORM_FEE = 249;
const GST_RATE = 0.18;
const TITLES = ["Mr", "Mrs", "Miss", "Ms"];

const MEAL_LABEL = {
  Room_Only: "Room only",
  BreakFast: "Breakfast included",
  Breakfast_For_2: "Breakfast for 2",
  Half_Board: "Half board",
  Full_Board: "Full board",
  All_Inclusive: "All inclusive",
};

/* ── Helpers ── */
const fmtPrice = (n) => {
  if (!n && n !== 0) return "";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "";
  const hasDecimal = num % 1 !== 0;
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

function fmtDate(s, withYear = true) {
  if (!s) return "";
  try {
    const [y, m, d] = s.split("-");
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
      weekday: "short", day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}),
    });
  } catch { return s; }
}

function nightsBetween(a, b) {
  try { return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000)) || 1; } catch { return 1; }
}

const parseBody = (raw) => (typeof raw?.body === "string" ? JSON.parse(raw.body) : raw);

async function postTBO(payload) {
  const r = await fetch(TBO_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseBody(await r.json());
}

/* ── Styles ── */
const CARET = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%235F6368' stroke-width='1.6'/%3E%3C/svg%3E")`;

const CSS = `
.rchb{font-size:16px;line-height:1.5;color:${INK};-webkit-font-smoothing:antialiased}
.rchb *,.rchb *::before,.rchb *::after{box-sizing:border-box}
.rchb button,.rchb input,.rchb select{font:inherit;color:inherit}
.rchb-full{background:#fff;min-height:100vh}
.rchb-overlay{position:fixed;inset:0;z-index:9000;background:rgba(17,17,17,.55);display:flex;align-items:flex-end;justify-content:center}
.rchb-sheet{background:#fff;width:100%;max-width:600px;max-height:94vh;overflow-y:auto;border-radius:12px 12px 0 0}

.rchb-wrap{max-width:1160px;margin:0 auto;padding:20px 32px 72px}
.rchb-sheet .rchb-wrap{padding:16px 20px 8px}
.rchb-topbar{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;min-height:32px}
.rchb-link{background:none;border:0;padding:6px 0;cursor:pointer;color:${MUTED};font-size:15px}
.rchb-link:hover{color:${INK}}

.rchb-h1{font-size:32px;line-height:1.2;font-weight:700;letter-spacing:-.015em;margin:0}
.rchb-sheet .rchb-h1{font-size:24px}
.rchb-sub{color:${MUTED};margin-top:6px;font-size:16px}

.rchb-photos{display:grid;grid-template-columns:2fr 1fr;grid-template-rows:200px 200px;gap:6px;margin:20px 0 36px;border-radius:8px;overflow:hidden}
.rchb-photos .p0{grid-row:1/3}
.rchb-photos.two{grid-template-rows:400px}
.rchb-photos.two .p0{grid-row:auto}
.rchb-photos.one{grid-template-columns:1fr;grid-template-rows:400px}
.rchb-photos.one .p0{grid-row:auto}
.rchb-sheet .rchb-photos{grid-template-rows:240px;margin:16px 0 24px}
.rchb-photo{position:relative;background:#F1F1F1;border:0;padding:0;cursor:zoom-in;overflow:hidden;display:block;width:100%;height:100%}
.rchb-photo img{width:100%;height:100%;object-fit:cover;display:block;transition:opacity .2s}
.rchb-photo:hover img{opacity:.92}
.rchb-photo-empty{background:#F3F3F3;color:${MUTED};font-size:15px;display:flex;align-items:center;justify-content:center}
.rchb-more{position:absolute;right:12px;bottom:12px;background:#fff;color:${INK};font-size:14px;font-weight:600;padding:7px 12px;border-radius:4px;box-shadow:0 1px 2px rgba(0,0,0,.2)}

.rchb-split{display:grid;grid-template-columns:minmax(0,1fr) 380px;gap:56px;align-items:start}
.rchb-aside{position:sticky;top:90px}
.rchb-sheet .rchb-split{display:block}
.rchb-sheet .rchb-aside{position:static;margin-top:4px}

.rchb-sec{padding:30px 0;border-top:1px solid ${LINE}}
.rchb-sec:first-child{border-top:0;padding-top:0}
.rchb-h2{font-size:22px;font-weight:700;margin:0 0 4px;letter-spacing:-.005em}
.rchb-note{color:${MUTED};font-size:15px;margin:0 0 18px}

.rchb-rooms{border:1px solid ${LINE};border-radius:8px;overflow:hidden}
.rchb-room{display:grid;grid-template-columns:22px minmax(0,1fr) auto;gap:14px;padding:18px 20px;cursor:pointer;border-top:1px solid ${LINE};transition:background .12s}
.rchb-room:first-child{border-top:0}
.rchb-room:hover{background:#FAFAFA}
.rchb-room.is-sel{background:#FFF6F1;box-shadow:inset 3px 0 0 ${ACCENT}}
.rchb-room input{accent-color:${ACCENT};width:18px;height:18px;margin:4px 0 0;cursor:pointer}
.rchb-room-name{font-size:17px;font-weight:600;line-height:1.35}
.rchb-room-meta{font-size:15px;color:${MUTED};margin-top:4px}
.rchb-room-promo{font-size:14px;color:#8A3B00;font-weight:600;margin-top:4px}
.rchb-room-incl{font-size:14px;color:${MUTED};margin-top:4px}
.rchb-room-price{text-align:right;white-space:nowrap}
.rchb-room-price b{font-size:20px;display:block;line-height:1.2}
.rchb-room-price span{font-size:13px;color:${MUTED}}
.rchb-ok{color:${OK}}
.rchb-bad{color:${BAD}}
.rchb-empty{border:1px dashed #CFCFCF;border-radius:8px;padding:28px;text-align:center;color:${MUTED};font-size:15px}

.rchb-form{display:grid;grid-template-columns:120px 1fr 1fr;gap:18px 16px}
.rchb-form .c12{grid-column:1/3}
.rchb-field label{display:block;font-size:14px;font-weight:600;margin-bottom:6px}
.rchb-field .opt{font-weight:400;color:${MUTED}}
.rchb-input{width:100%;height:48px;padding:0 14px;border:1px solid #C6C6C6;border-radius:6px;background:#fff;font-size:16px;outline:none;transition:border-color .1s,box-shadow .1s}
.rchb-input::placeholder{color:#9AA0A6}
.rchb-input:focus{border-color:${INK};box-shadow:0 0 0 1px ${INK}}
select.rchb-input{appearance:none;-webkit-appearance:none;padding-right:34px;background-image:${CARET};background-repeat:no-repeat;background-position:right 12px center;cursor:pointer}
.rchb-hint{font-size:13px;color:${MUTED};margin-top:5px}

.rchb-list{margin:0;padding:0;list-style:none;font-size:15px}
.rchb-list li{padding:5px 0}
.rchb-facs{columns:2;column-gap:32px}
.rchb-facs li{break-inside:avoid}

.rchb-card{border:1px solid ${LINE};border-radius:8px;padding:22px}
.rchb-k{font-size:12px;text-transform:uppercase;letter-spacing:.07em;color:${MUTED};font-weight:600}
.rchb-v{font-size:16px;font-weight:600;margin-top:2px}
.rchb-dates{display:grid;grid-template-columns:1fr 1fr;border:1px solid ${LINE};border-radius:6px;margin:16px 0 14px}
.rchb-dates>div{padding:12px 14px}
.rchb-dates>div+div{border-left:1px solid ${LINE}}
.rchb-dates small{display:block;font-size:13px;color:${MUTED};margin-top:1px}
.rchb-row{display:flex;justify-content:space-between;gap:16px;padding:7px 0;font-size:15px}
.rchb-row>span:first-child{color:${MUTED}}
.rchb-row>span:last-child{text-align:right}
.rchb-toggle{background:none;border:0;padding:0;cursor:pointer;color:${MUTED};text-decoration:underline;text-underline-offset:3px;text-decoration-color:#BDBDBD}
.rchb-subrows{padding-left:12px;border-left:2px solid ${LINE};margin:2px 0 6px}
.rchb-subrows .rchb-row{font-size:14px;padding:4px 0}
.rchb-total{display:flex;justify-content:space-between;align-items:baseline;border-top:1px solid ${LINE};margin-top:10px;padding-top:14px;font-weight:600;font-size:17px}
.rchb-total b{font-size:24px;font-weight:700}

.rchb-pay{width:100%;height:52px;margin-top:18px;border:0;border-radius:6px;background:${ACCENT};color:#fff;font-weight:600;font-size:17px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .12s}
.rchb-pay:hover:not(:disabled){background:${ACCENT_DARK}}
.rchb-pay:disabled{background:#D5D5D5;cursor:not-allowed}
.rchb-fine{font-size:13px;color:${MUTED};margin-top:10px;text-align:center}
.rchb-err{font-size:14px;color:${BAD};background:#FCEEEE;border-radius:6px;padding:10px 12px;margin-top:14px}

.rchb-mbar{display:none;flex-wrap:wrap;align-items:center;gap:10px 16px;background:#fff;border-top:1px solid ${LINE};padding:12px 16px calc(12px + env(safe-area-inset-bottom))}
.rchb-mbar .rchb-pay{margin:0;width:auto;flex:1;min-width:160px}
.rchb-mbar .rchb-err{width:100%;margin:0}
.rchb-mbar-total{font-size:13px;color:${MUTED};line-height:1.2}
.rchb-mbar-total b{display:block;font-size:20px;color:${INK}}
.rchb-full .rchb-mbar{position:fixed;left:0;right:0;bottom:0;z-index:50}
.rchb-sheet .rchb-mbar{display:flex;position:sticky;bottom:0;margin-top:8px}
.rchb-sheet .rchb-aside .rchb-pay,.rchb-sheet .rchb-aside .rchb-fine,.rchb-sheet .rchb-aside .rchb-err{display:none}

.rchb-state{max-width:520px;margin:0 auto;padding:64px 8px;text-align:center}
.rchb-state h2{font-size:26px;line-height:1.25;margin:22px 0 8px}
.rchb-state p{color:${MUTED};font-size:16px;margin:0}
.rchb-warnbox{margin:28px auto 0;font-size:15px;border-left:3px solid ${ACCENT};padding:10px 14px;text-align:left;background:#FAFAFA;max-width:420px}
.rchb-spin{animation:rchb-spin .9s linear infinite;display:block}
@keyframes rchb-spin{to{transform:rotate(360deg)}}

@media (max-width:960px){
  .rchb-split{grid-template-columns:1fr;gap:8px}
  .rchb-aside{position:static}
  .rchb-full .rchb-mbar{display:flex}
  .rchb-full .rchb-aside .rchb-pay,.rchb-full .rchb-aside .rchb-fine,.rchb-full .rchb-aside .rchb-err{display:none}
  .rchb-full .rchb-wrap{padding-bottom:130px}
}
@media (max-width:640px){
  .rchb-wrap{padding:14px 16px 130px}
  .rchb-h1{font-size:26px}
  .rchb-photos,.rchb-photos.two{grid-template-columns:1fr;grid-template-rows:240px}
  .rchb-photos .p0{grid-row:auto}
  .rchb-photos .p1,.rchb-photos .p2{display:none}
  .rchb-facs{columns:1}
  .rchb-form{grid-template-columns:100px 1fr}
  .rchb-form>*{grid-column:1/-1}
  .rchb-form>.f-title{grid-column:1/2}
  .rchb-form>.f-first{grid-column:2/3}
  .rchb-form .c12{grid-column:1/-1}
  .rchb-room{grid-template-columns:22px 1fr;padding:16px}
  .rchb-room-price{grid-column:2;text-align:left;margin-top:-4px}
}
`;

/* ── Spinner ── */
function Spin({ size = 28, color = ACCENT, track = "#E6E6E6" }) {
  return (
    <svg viewBox="0 0 24 24" className="rchb-spin" style={{ width: size, height: size }}>
      <circle cx="12" cy="12" r="10" fill="none" stroke={track} strokeWidth="2.5" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/* ── Lightbox ── */
function Lightbox({ images, startIndex, onClose }) {
  const [i, setI] = useState(startIndex);
  const n = images.length;
  const prev = () => setI(p => (p - 1 + n) % n);
  const next = () => setI(p => (p + 1) % n);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [n, onClose]);

  const navBtn = {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    background: "#fff", border: 0, color: INK, width: 44, height: 44, borderRadius: 4,
    cursor: "pointer", fontSize: 22, lineHeight: 1,
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 10000, background: "rgba(10,10,10,.94)",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, color: "#fff",
    }}>
      <div style={{ position: "absolute", top: 18, left: 24, opacity: 0.8 }}>{i + 1} / {n}</div>
      <button onClick={onClose} style={{
        position: "absolute", top: 12, right: 18, background: "none", border: 0, color: "#fff",
        fontSize: 15, cursor: "pointer", padding: "8px 10px",
      }}>Close</button>
      {n > 1 && <button aria-label="Previous" onClick={e => { e.stopPropagation(); prev(); }} style={{ ...navBtn, left: 20 }}>‹</button>}
      <img src={images[i]} alt="" onClick={e => e.stopPropagation()}
        style={{ maxWidth: "88vw", maxHeight: "84vh", objectFit: "contain" }} />
      {n > 1 && <button aria-label="Next" onClick={e => { e.stopPropagation(); next(); }} style={{ ...navBtn, right: 20 }}>›</button>}
    </div>
  );
}

/* ── Photo grid ── */
function Photos({ images, compact, loading, onOpen }) {
  const [failed, setFailed] = useState({});
  const ok = (images || []).filter(u => !failed[u]);

  if (!ok.length) {
    return (
      <div className="rchb-photos one">
        <div className="rchb-photo-empty">{loading ? <Spin size={28} /> : "Photos not available for this property"}</div>
      </div>
    );
  }

  const shown = ok.slice(0, compact ? 1 : 3);
  const cls = shown.length === 1 ? "one" : shown.length === 2 ? "two" : "";

  return (
    <div className={`rchb-photos ${cls}`}>
      {shown.map((src, j) => (
        <button key={src} type="button" className={`rchb-photo p${j}`} onClick={() => onOpen(ok, j)}>
          <img src={src} alt="" loading={j === 0 ? "eager" : "lazy"}
            onError={() => setFailed(f => ({ ...f, [src]: true }))} />
          {j === shown.length - 1 && ok.length > shown.length && (
            <span className="rchb-more">Show all {ok.length} photos</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ── Room row ── */
function RoomRow({ room, selected, onSelect, nights }) {
  const name = Array.isArray(room.Name) ? room.Name.join(", ") : (room.name || "Room");
  const fare = room.TotalFare ?? room.total_fare ?? 0;
  const tax = room.TotalTax ?? room.total_tax ?? 0;
  const meal = room.MealType ?? room.meal_type ?? "Room_Only";
  const refundable = room.IsRefundable ?? room.is_refundable ?? false;
  const promos = room.RoomPromotion || room.promotions || [];
  const incl = room.Inclusion || room.inclusion || "";
  const deadline = room.LastCancellationDeadline;
  const inclList = incl.split(",").map(s => s.trim()).filter(Boolean).slice(0, 4);

  return (
    <label className={`rchb-room${selected ? " is-sel" : ""}`}>
      <input type="radio" name="rchb-room" checked={selected} onChange={onSelect} />
      <div>
        <div className="rchb-room-name">{name}</div>
        <div className="rchb-room-meta">
          {MEAL_LABEL[meal] || String(meal).replace(/_/g, " ")}
          {" · "}
          {refundable
            ? <span className="rchb-ok">Free cancellation{deadline ? ` until ${deadline.split(" ")[0]}` : ""}</span>
            : <span className="rchb-bad">Non-refundable</span>}
        </div>
        {promos.length > 0 && <div className="rchb-room-promo">{promos.join(", ")}</div>}
        {inclList.length > 0 && <div className="rchb-room-incl">{inclList.join(", ")}</div>}
      </div>
      <div className="rchb-room-price">
        <b>₹{fmtPrice(fare)}</b>
        <span>{nights} night{nights !== 1 ? "s" : ""}{tax > 0 ? ` + ₹${fmtPrice(tax)} taxes` : ""}</span>
      </div>
    </label>
  );
}

/* ── Form field ── */
function Field({ label, optional, hint, className = "", children }) {
  return (
    <div className={`rchb-field ${className}`}>
      <label>{label}{optional && <span className="opt"> (optional)</span>}</label>
      {children}
      {hint && <div className="rchb-hint">{hint}</div>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", uppercase, autoComplete, maxLength }) {
  return (
    <input className="rchb-input" type={type} value={value} placeholder={placeholder}
      autoComplete={autoComplete} maxLength={maxLength}
      onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)} />
  );
}

/* ── Confirmation ── */
function Confirmation({ booking, onClose }) {
  const st = booking?.status || "";
  const ok = st === "BOOKING_CONFIRMED" || booking?.payment_status === "PAID" || st.includes("CONFIRM");
  const pnd = !ok && (st === "PAYMENT_PENDING" || booking?.payment_status === "PENDING");
  const tone = ok ? OK : pnd ? WARN : BAD;

  const rows = [
    ["Hotel", booking?.hotel_name],
    ["Room", booking?.room_type_name],
    ["Check-in", fmtDate(booking?.check_in_date)],
    ["Check-out", fmtDate(booking?.check_out_date)],
    booking?.booking_confirmation_no && ["Confirmation no.", booking.booking_confirmation_no],
    booking?.booking_ref_no && ["Reference no.", booking.booking_ref_no],
    ["Booking ID", booking?.rc_booking_id],
    booking?.amount ? ["Amount paid", `₹${fmtPrice(booking.amount)}`] : null,
  ].filter(r => r && r[1]);

  return (
    <div className="rchb-state" style={{ textAlign: "left", paddingTop: 40 }}>
      <div className="rchb-k" style={{ color: tone }}>
        {ok ? "Confirmed" : pnd ? "Awaiting confirmation" : "Not completed"}
      </div>
      <h2 style={{ marginTop: 6 }}>
        {ok ? "Your stay is booked" : pnd ? "We're still confirming your booking" : "We couldn't complete this booking"}
      </h2>
      <p>
        {ok ? `A confirmation has been sent to your email. Show it at the front desk of ${booking?.hotel_name || "the hotel"} on arrival.`
          : pnd ? "If your payment went through, you'll get a confirmation email shortly. You can also check this booking from My Trips."
            : "If any amount was debited, it will be refunded to your original payment method within 5 to 7 working days."}
      </p>
      {(ok || pnd) && rows.length > 0 && (
        <div className="rchb-card" style={{ marginTop: 24, padding: "10px 18px" }}>
          {rows.map(([k, v]) => (
            <div key={k} className="rchb-row" style={{ borderTop: k === rows[0][0] ? 0 : `1px solid ${LINE}`, padding: "10px 0" }}>
              <span>{k}</span><span style={{ fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
      )}
      <button className="rchb-pay" onClick={onClose} style={{ marginTop: 24 }}>Done</button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════ */
export default function HotelBooking({ hotel, userId, hotelTest = true, onClose, fullPage = false }) {
  const [phase, setPhase] = useState("details"); // details|processing|polling|done
  const [selRoom, setSelRoom] = useState(null);
  const [images, setImages] = useState([]);
  const [facilities, setFacs] = useState([]);
  const [validation, setVal] = useState(null);       // prebook ValidationInfo
  const [policies, setPols] = useState([]);          // CancelPolicies
  const [detailsBid, setDetailsBid] = useState(null); // rc_booking_id from hotel_details
  const [finalBk, setFinalBk] = useState(null);
  const [statusMsg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [lb, setLb] = useState(null);                // { list, idx }
  const pollingRef = useRef(null);

  const [g, setG] = useState({
    Title: "", FirstName: "", LastName: "", Email: "", Phoneno: "",
    PAN: "", Passport: "",
  });
  const setField = (k) => (v) => setG(p => ({ ...p, [k]: v }));

  const hotelCode = hotel?.hotel_code;
  const checkIn = hotel?.check_in || "";
  const checkOut = hotel?.check_out || "";
  const nights = nightsBetween(checkIn, checkOut);
  const hName = hotel?.hotel_name || "Hotel";
  const userFrom = `user:${userId}`;

  // Never let room count fall to 0 / NaN
  const NO_OF_ROOMS = Math.max(1, parseInt(hotel?.no_of_rooms ?? hotel?.NoOfRooms ?? 1, 10) || 1);

  const [rooms, setRooms] = useState(hotel?.rooms || []);
  const [roomsState, setRoomsState] = useState(hotel?.rooms?.length ? "ready" : "loading");
  const [resolvedCityName, setResolvedCityName] = useState(null);
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  // Pre-select room passed from itinerary
  useEffect(() => {
    if (!selRoom && rooms.length) {
      const pre = hotel?.selected_room;
      const match = pre
        ? rooms.find(r => (r.BookingCode || r.booking_code) === (pre.booking_code || pre.BookingCode)) || rooms[0]
        : rooms[0];
      setSelRoom(match);
    }
  }, [rooms]);

  // Load hotel details (images, facilities, city, rc_booking_id) from TBO
  useEffect(() => {
    if (!hotelCode) { setDetailsLoaded(true); return; }
    (async () => {
      try {
        const body = await postTBO({ intent: "hotel_details", hotel_code: hotelCode, hotel_test: hotelTest, From: userFrom });
        if (body.status === "error" || body.http_code === 500) {
          console.warn("[HotelBooking] hotel_details returned error:", body.message || body);
          return;
        }
        const hd = body.hotel_details || body.data?.HotelDetails?.[0] || {};
        const imgs = [...new Set([hd.Image, ...(hd.Images || [])].filter(Boolean))];
        setImages(imgs);
        setFacs((hd.HotelFacilities || []).slice(0, 16));
        if (hd.CityName) setResolvedCityName(`${hd.CityName}${hd.CountryName ? ", " + hd.CountryName : ""}`);
        const rid = body.rc_booking_id || body.data?.rc_booking_id;
        if (rid) setDetailsBid(rid);
      } catch (e) {
        console.error("[HotelBooking] hotel_details fetch failed:", e);
      } finally {
        setDetailsLoaded(true);
      }
    })();
  }, [hotelCode]);

  // If rooms not passed, fetch from /hotels once hotel_details has resolved
  useEffect(() => {
    if (rooms.length > 0) return;
    if (!hotelCode) { setRoomsState("empty"); return; }
    if (!detailsLoaded) return;
    (async () => {
      try {
        const toApiDate = (iso) => {
          if (!iso) {
            const d = new Date(); d.setDate(d.getDate() + 1);
            return [String(d.getDate()).padStart(2, "0"), String(d.getMonth() + 1).padStart(2, "0"), d.getFullYear()].join("-");
          }
          const [y, m, d] = iso.split("-");
          return `${d}-${m}-${y}`;
        };
        const cin = toApiDate(checkIn);
        const cout = checkOut
          ? toApiDate(checkOut)
          : (() => { const d = new Date(); d.setDate(d.getDate() + 2); return toApiDate(d.toISOString().split("T")[0]); })();

        const placeName = hotel?.hotel_area || hotel?.city_name || resolvedCityName || hotel?.hotel_name;
        if (!placeName) {
          setErr("We couldn't load rooms for this hotel. Please go back and open it again from your trip.");
          return;
        }

        const resp = await fetch(`${LAMBDA_URL}/hotels`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            place_name: placeName,
            check_in: cin, check_out: cout,
            radius_km: 30, user_id: userId,
            no_of_rooms: NO_OF_ROOMS,
          }),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const hotels = data.hotels || [];
        const match = hotels.find(h => h.hotel_code === hotelCode) || hotels[0];
        if (match?.rooms?.length) setRooms(match.rooms);
      } catch { /* handled by empty state */ }
      finally {
        setRoomsState(s => (s === "loading" ? "done" : s));
      }
    })();
  }, [hotelCode, detailsLoaded, resolvedCityName]);

  useEffect(() => { if (rooms.length) setRoomsState("ready"); }, [rooms]);

  const bookingCode = selRoom?.BookingCode || selRoom?.booking_code || "";
  const roomName = selRoom ? (Array.isArray(selRoom.Name) ? selRoom.Name.join(", ") : (selRoom.name || "")) : "";
  const fareAmt = selRoom ? (selRoom.TotalFare ?? selRoom.total_fare ?? 0) : 0;
  const fareTax = selRoom ? (selRoom.TotalTax ?? selRoom.total_tax ?? 0) : 0;
  const gst = Math.round(PLATFORM_FEE * GST_RATE);
  const total = Math.round(fareAmt + fareTax + PLATFORM_FEE + gst);

  const passportReq = validation?.PassportMandatory ?? false;

  function validate() {
    if (!g.Title) return "Please select a title (Mr, Mrs, Miss or Ms).";
    if (!g.FirstName.trim() || !g.LastName.trim()) return "Please enter the guest's first and last name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.Email.trim())) return "Please enter a valid email address.";
    if (g.Phoneno.replace(/\D/g, "").length < 10) return "Please enter a valid phone number.";
    const pan = g.PAN.trim();
    if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return "That PAN doesn't look right. Use the format ABCDE1234F, or leave it blank.";
    if (passportReq && !g.Passport.trim()) return "This hotel needs a passport number for the lead guest.";
    return null;
  }

  // ── Prebook -> CreateOrder ─────────────────────────────
  async function handlePay() {
    const v = validate();
    if (v) { setErr(v); return; }
    if (!selRoom) { setErr("Please select a room."); return; }
    setErr(""); setBusy(true); setPhase("processing");
    setMsg("Checking room availability");

    try {
      let rcId = detailsBid;

      // Step 1: Prebook
      const pre = await postTBO({
        intent: "prebook_hotel", hotel_test: hotelTest, From: userFrom,
        booking_code: bookingCode,
        no_of_rooms: NO_OF_ROOMS, NoOfRooms: NO_OF_ROOMS,
        ...(rcId ? { rc_booking_id: rcId, ClientReferenceId: rcId } : {}),
      });
      if (pre.status !== 200 && pre.status !== "success") throw new Error(pre.message || "This room is no longer available. Please pick another.");

      const hr = pre.data?.HotelResult?.[0] || pre.HotelResult?.[0];
      const vi = pre.data?.ValidationInfo || pre.ValidationInfo || hr?.ValidationInfo;
      if (vi) setVal(vi);
      const cp = hr?.Rooms?.[0]?.CancelPolicies || [];
      if (cp.length) setPols(cp);

      const preRid = pre.rc_booking_id || pre.data?.rc_booking_id;
      if (!rcId) rcId = preRid || null;
      else if (preRid && preRid !== rcId) console.warn("[HotelBooking] prebook rc_booking_id differs from hotel_details; using hotel_details id", { detailsBid: rcId, preRid });
      if (!rcId) throw new Error("We couldn't create a booking reference. Please reopen this hotel and try again.");

      if (vi?.PassportMandatory && !g.Passport.trim()) {
        setErr("This hotel needs a passport number for the lead guest. Please add it below.");
        setPhase("details");
        return;
      }

      // Step 2: Create order
      setMsg("Creating your order");
      const guests = [{
        Title: g.Title,
        FirstName: g.FirstName.trim(),
        LastName: g.LastName.trim(),
        Email: g.Email.trim(),
        Phoneno: g.Phoneno.trim(),
        PaxType: 1,
        LeadPassenger: true,
        ...(g.PAN.trim() ? { PAN: g.PAN.trim().toUpperCase() } : {}),
        ...(g.Passport.trim() ? { Passport: g.Passport.trim().toUpperCase() } : {}),
      }];

      const ord = await postTBO({
        intent: "create_order_hotel", hotel_test: hotelTest, From: userFrom,
        rc_booking_id: rcId,
        ClientReferenceId: rcId,
        booking_code: bookingCode,
        hotel_name: hName, hotel_code: hotelCode,
        room_type_name: roomName,
        check_in_date: checkIn, check_out_date: checkOut,
        no_of_rooms: NO_OF_ROOMS, NoOfRooms: NO_OF_ROOMS,
        guests, currency: "INR",
      });
      if (ord.status !== 200) throw new Error(ord.message || "We couldn't create your order. Please try again.");

      await launchRazorpay(ord.data || ord, rcId);
    } catch (ex) {
      setErr(ex.message || "Something went wrong. Please try again.");
      setPhase("details");
    } finally {
      setBusy(false);
    }
  }

  // ── Razorpay ───────────────────────────────────────────
  async function launchRazorpay(orderData, rcBid) {
    const shortUrl = orderData.short_url || orderData.rzp_short_url;
    if (shortUrl) { window.open(shortUrl, "_blank"); setPhase("polling"); startPolling(rcBid); return; }
    const rzpId = orderData.rzp_order_id;
    const rzpKey = orderData.rzp_key_id;
    if (!rzpId || !rzpKey) throw new Error("Payment details are missing. Please try again.");
    await new Promise((res, rej) => {
      if (window.Razorpay) return res();
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = res;
      s.onerror = () => rej(new Error("Couldn't load the payment window. Check your connection and try again."));
      document.body.appendChild(s);
    });
    setMsg("Opening payment");
    const opts = {
      key: rzpKey,
      amount: Math.round((orderData.amount || 0) * 100),
      currency: orderData.currency || "INR",
      name: "RouteChef",
      description: `${hName}, ${fmtDate(checkIn, false)}`,
      order_id: rzpId,
      prefill: { name: `${g.FirstName} ${g.LastName}`, email: g.Email, contact: g.Phoneno },
      theme: { color: ACCENT },
      handler: () => { setPhase("polling"); startPolling(rcBid); },
      modal: { ondismiss: () => { setPhase("details"); setMsg(""); } },
    };
    new window.Razorpay(opts).open();
  }

  // ── Poll ───────────────────────────────────────────────
  function startPolling(rcBid) {
    setMsg("This usually takes under a minute.");
    let tries = 0;
    clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      tries++;
      try {
        const body = await postTBO({ intent: "get_status", hotel_test: hotelTest, From: userFrom, rc_booking_id: rcBid });
        const bk = body.data?.bookings?.[0] || body.bookings?.[0];
        const st = bk?.status || bk?.payment_status || "";
        if (st === "PAID" || st.includes("CONFIRM")) {
          clearInterval(pollingRef.current);
          setFinalBk({ ...bk, rc_booking_id: rcBid }); setPhase("done"); return;
        }
        if (["BOOKING_FAILED", "FAILED", "CANCELLED"].includes(st)) {
          clearInterval(pollingRef.current);
          setFinalBk({ ...bk, rc_booking_id: rcBid, status: "BOOKING_FAILED" }); setPhase("done"); return;
        }
      } catch { /* keep polling */ }
      if (tries >= 20) {
        clearInterval(pollingRef.current);
        setFinalBk({ rc_booking_id: rcBid, status: "PAYMENT_PENDING", hotel_name: hName, room_type_name: roomName, check_in_date: checkIn, check_out_date: checkOut });
        setPhase("done");
      }
    }, 3000);
  }
  useEffect(() => () => clearInterval(pollingRef.current), []);

  /* ─── RENDER ─────────────────────────────────────────── */
  const location = hotel?.city_name || hotel?.hotel_area || resolvedCityName || "";
  const ratingNum = Math.min(5, Math.round(Number(hotel?.hotel_rating) || 0));
  const subline = [location, ratingNum ? `${ratingNum}-star hotel` : ""].filter(Boolean).join("  ·  ");

  const payBtn = (
    <button className="rchb-pay" onClick={handlePay} disabled={!selRoom || busy}>
      {busy ? <><Spin size={18} color="#fff" track="rgba(255,255,255,.35)" /> Processing</>
        : selRoom ? `Pay ₹${fmtPrice(total)}` : "Select a room"}
    </button>
  );

  const sheetTitle = phase === "done" ? "Booking summary" : phase === "polling" ? "Confirming booking" : "Book hotel";

  const summary = (
    <div className="rchb-card">
      <div className="rchb-k">Your stay</div>
      <div className="rchb-v" style={{ fontSize: 17 }}>{hName}</div>

      {checkIn && (
        <div className="rchb-dates">
          <div>
            <div className="rchb-k">Check-in</div>
            <div className="rchb-v">{fmtDate(checkIn, false)}</div>
            <small>From {(hotel?.checkin_time || "12:00").slice(0, 5)}</small>
          </div>
          <div>
            <div className="rchb-k">Check-out</div>
            <div className="rchb-v">{fmtDate(checkOut, false)}</div>
            <small>Until {(hotel?.checkout_time || "11:00").slice(0, 5)}</small>
          </div>
        </div>
      )}

      <div className="rchb-row"><span>Room</span><span>{roomName || "Not selected"}</span></div>
      <div className="rchb-row"><span>Length of stay</span><span>{nights} night{nights !== 1 ? "s" : ""}</span></div>

      {selRoom && (
        <>
          <div style={{ borderTop: `1px solid ${LINE}`, margin: "10px 0 4px" }} />
          <div className="rchb-row"><span>Room charge</span><span>₹{fmtPrice(fareAmt)}</span></div>
          <div className="rchb-row">
            <span><button type="button" className="rchb-toggle" onClick={() => setTaxOpen(o => !o)}>Taxes and fees</button></span>
            <span>₹{fmtPrice(fareTax + PLATFORM_FEE + gst)}</span>
          </div>
          {taxOpen && (
            <div className="rchb-subrows">
              {fareTax > 0 && <div className="rchb-row"><span>Hotel taxes</span><span>₹{fmtPrice(fareTax)}</span></div>}
              <div className="rchb-row"><span>Platform fee</span><span>₹{PLATFORM_FEE}</span></div>
              <div className="rchb-row"><span>GST on platform fee (18%)</span><span>₹{gst}</span></div>
            </div>
          )}
          <div className="rchb-total"><span>Total</span><b>₹{fmtPrice(total)}</b></div>
        </>
      )}

      {err && <div className="rchb-err">{err}</div>}
      {payBtn}
      <div className="rchb-fine">Payment is processed securely by Razorpay.</div>
    </div>
  );

  const wrap = (
    <div className="rchb-wrap">
      <div className="rchb-topbar">
        {fullPage
          ? <button className="rchb-link" onClick={onClose}>← Back</button>
          : <strong style={{ fontSize: 18 }}>{sheetTitle}</strong>}
        {!fullPage && <button className="rchb-link" onClick={onClose}>Close</button>}
      </div>

      {phase === "processing" && (
        <div className="rchb-state">
          <div style={{ display: "flex", justifyContent: "center" }}><Spin size={44} /></div>
          <h2>Processing your booking</h2>
          <p>{statusMsg || "Please wait"}</p>
          <div className="rchb-warnbox">Please don't refresh or go back until this finishes.</div>
        </div>
      )}

      {phase === "polling" && (
        <div className="rchb-state">
          <div style={{ display: "flex", justifyContent: "center" }}><Spin size={44} /></div>
          <h2>Confirming your booking with the hotel</h2>
          <p>{statusMsg}</p>
          <div className="rchb-warnbox">Please keep this page open.</div>
        </div>
      )}

      {phase === "done" && <Confirmation booking={finalBk} onClose={onClose} />}

      {phase === "details" && (
        <>
          <header>
            <h1 className="rchb-h1">{hName}</h1>
            {subline && <div className="rchb-sub">{subline}</div>}
          </header>

          <Photos images={images} compact={!fullPage} loading={!detailsLoaded}
            onOpen={(list, idx) => setLb({ list, idx })} />

          <div className="rchb-split">
            <main>
              {/* Rooms */}
              <section className="rchb-sec">
                <h2 className="rchb-h2">Choose your room</h2>
                <p className="rchb-note">
                  {checkIn
                    ? `${fmtDate(checkIn, false)} to ${fmtDate(checkOut, false)}, ${nights} night${nights !== 1 ? "s" : ""}`
                    : "Prices shown are for your full stay."}
                </p>
                {rooms.length > 0 ? (
                  <div className="rchb-rooms" role="radiogroup">
                    {rooms.map((r, i) => (
                      <RoomRow key={r.BookingCode || r.booking_code || i} room={r} nights={nights}
                        selected={(r.BookingCode || r.booking_code) === (selRoom?.BookingCode || selRoom?.booking_code)}
                        onSelect={() => setSelRoom(r)} />
                    ))}
                  </div>
                ) : roomsState === "loading" ? (
                  <div className="rchb-empty" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                    <Spin size={20} /> Checking availability
                  </div>
                ) : (
                  <div className="rchb-empty">No rooms are available for these dates. Try different dates or another hotel.</div>
                )}
              </section>

              {/* Guest */}
              <section className="rchb-sec">
                <h2 className="rchb-h2">Lead guest</h2>
                <p className="rchb-note">Enter the name exactly as it appears on the ID you'll show at check-in.</p>
                <div className="rchb-form">
                  <Field label="Title" className="f-title">
                    <select className="rchb-input" value={g.Title} onChange={e => setField("Title")(e.target.value)}
                      style={{ color: g.Title ? INK : "#9AA0A6" }}>
                      <option value="" disabled>Select</option>
                      {TITLES.map(t => <option key={t} value={t} style={{ color: INK }}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="First name" className="f-first">
                    <Input value={g.FirstName} onChange={setField("FirstName")} placeholder="Aarav" autoComplete="given-name" />
                  </Field>
                  <Field label="Last name">
                    <Input value={g.LastName} onChange={setField("LastName")} placeholder="Sharma" autoComplete="family-name" />
                  </Field>
                  <Field label="Email" className="c12" hint="We'll send the booking confirmation here.">
                    <Input type="email" value={g.Email} onChange={setField("Email")} placeholder="aarav@example.com" autoComplete="email" />
                  </Field>
                  <Field label="Mobile number">
                    <Input type="tel" value={g.Phoneno} onChange={setField("Phoneno")} placeholder="+91 98765 43210" autoComplete="tel" />
                  </Field>
                  <Field label="PAN" optional className="c12">
                    <Input value={g.PAN} onChange={setField("PAN")} placeholder="ABCDE1234F" uppercase maxLength={10} />
                  </Field>
                  {passportReq && (
                    <Field label="Passport number">
                      <Input value={g.Passport} onChange={setField("Passport")} placeholder="A1234567" uppercase />
                    </Field>
                  )}
                </div>
              </section>

              {/* Cancellation */}
              {policies.length > 0 && (
                <section className="rchb-sec">
                  <h2 className="rchb-h2">Cancellation policy</h2>
                  <ul className="rchb-list" style={{ marginTop: 12 }}>
                    {policies.map((p, i) => (
                      <li key={i}>
                        From {p.FromDate?.split(" ")[0]}:{" "}
                        {p.CancellationCharge === 0
                          ? <span className="rchb-ok">free cancellation</span>
                          : p.ChargeType === "Percentage"
                            ? `${p.CancellationCharge}% of the booking amount`
                            : `₹${fmtPrice(Math.round(p.CancellationCharge))} cancellation charge`}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Amenities */}
              {facilities.length > 0 && (
                <section className="rchb-sec">
                  <h2 className="rchb-h2">Amenities</h2>
                  <ul className="rchb-list rchb-facs" style={{ marginTop: 12 }}>
                    {facilities.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </section>
              )}
            </main>

            <aside className="rchb-aside">{summary}</aside>
          </div>
        </>
      )}
    </div>
  );

  const mbar = phase === "details" && (
    <div className="rchb-mbar">
      {err && <div className="rchb-err">{err}</div>}
      {selRoom && <div className="rchb-mbar-total">Total<b>₹{fmtPrice(total)}</b></div>}
      {payBtn}
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      {fullPage ? (
        <div className="rchb rchb-full">{wrap}{mbar}</div>
      ) : (
        <div className="rchb-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
          <div className="rchb rchb-sheet">{wrap}{mbar}</div>
        </div>
      )}
      {lb && <Lightbox images={lb.list} startIndex={lb.idx} onClose={() => setLb(null)} />}
    </>
  );
}
